import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { MotionConfig, useReducedMotion } from "framer-motion";
import type { CSSProperties } from "react";
import { SnakContext, type Detail, type RefTarget, type SnakApi } from "./snak/SnakContext";
import { PAGES } from "./data/pages";
import { TopBar } from "./components/TopBar";
import { Controls } from "./components/Controls";
import { TransitionLabel } from "./components/TransitionLabel";
import { DetailSheet } from "./components/DetailSheet";
import { ReferencesSheet } from "./components/ReferencesSheet";
import { ChapterSheet } from "./components/ChapterSheet";
import { P1Evidence } from "./pages/P1Evidence";
import { P2Outcomes } from "./pages/P2Outcomes";
import { P3Patients } from "./pages/P3Patients";
import { P4WhichPatients } from "./pages/P4WhichPatients";
import { P5CloserLook } from "./pages/P5CloserLook";
import { P6Treatment } from "./pages/P6Treatment";
import { P7Consequence } from "./pages/P7Consequence";
import { P8Invitation } from "./pages/P8Invitation";
const PAGE_COMPONENTS = [P1Evidence, P2Outcomes, P3Patients, P4WhichPatients, P5CloserLook, P6Treatment, P7Consequence, P8Invitation];
export function App() {
  const reduced = !!useReducedMotion();
  const [page, setPage] = useState(0);
  // The HCP drives by default; auto-play is an opt-in guided mode.
  const [playing, setPlaying] = useState(false);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [dir, setDir] = useState(1);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [pausedByExplore, setPausedByExplore] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [refs, setRefs] = useState<{
    open: boolean;
    target: RefTarget;
  }>({
    open: false,
    target: null
  });
  const last = PAGES.length - 1;
  const duration = PAGES[page].duration;
  const ticking = playing && !detail && !refs.open && !chaptersOpen && page < last;

  // Auto-advance clock
  const lastT = useRef<number | null>(null);
  useEffect(() => {
    if (!ticking) {
      lastT.current = null;
      return;
    }
    let raf = 0;
    const loop = (t: number) => {
      if (lastT.current != null) {
        const dt = t - lastT.current;
        setElapsed(e => e + dt);
      }
      lastT.current = t;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [ticking, page]);
  useEffect(() => {
    if (ticking && elapsed >= duration) {
      setDir(1);
      setPage(p => Math.min(p + 1, last));
      setElapsed(0);
    }
  }, [elapsed, duration, ticking, last]);
  const go = useCallback((n: number) => {
    const target = Math.max(0, Math.min(last, n));
    setPage(p => {
      setDir(target >= p ? 1 : -1);
      return target;
    });
    setElapsed(0);
    setHasNavigated(true);
  }, [last]);
  const api: SnakApi = useMemo(() => ({
    page,
    total: PAGES.length,
    playing,
    reduced,
    go,
    next: () => go(page + 1),
    prev: () => go(page - 1),
    togglePlay: () => {
      if (page === last) {
        go(0);
        setPlaying(true);
        setPausedByExplore(false);
        return;
      }
      setPlaying(p => !p);
      setPausedByExplore(false);
    },
    explore: () => {
      setPlaying(false);
      setPausedByExplore(true);
    },
    openDetail: (d: Detail) => {
      setDetail(d);
      setPlaying(false);
      setPausedByExplore(true);
    },
    openRefs: (target: RefTarget = null) => {
      setRefs({
        open: true,
        target
      });
      setPlaying(false);
      setPausedByExplore(true);
    },
    openChapters: () => setChaptersOpen(true)
  }), [page, playing, reduced, go, last]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (detail || refs.open || chaptersOpen) {
        if (e.key === "Escape") {
          setDetail(null);
          setChaptersOpen(false);
          setRefs(r => ({
            ...r,
            open: false
          }));
        }
        return;
      }
      if (e.key === "ArrowRight") api.next();else if (e.key === "ArrowLeft") api.prev();else if (e.key === " " && (e.target as HTMLElement)?.tagName !== "BUTTON") {
        e.preventDefault();
        api.togglePlay();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [api, detail, refs.open, chaptersOpen]);

  // Swipe between chapters on touch devices
  const touch = useRef<{
    x: number;
    y: number;
    skip: boolean;
  } | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0];
    const skip = !!(e.target as HTMLElement).closest("[data-noswipe]");
    touch.current = {
      x: t.clientX,
      y: t.clientY,
      skip
    };
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    if (!start || start.skip) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.6) {
      if (dx < 0) api.next();else api.prev();
    }
  };
  const PageComponent = PAGE_COMPONENTS[page];
  const pageElement = useMemo(() => <PageComponent data-id="element-137" data-mp="3q6dqrr5qhnwu" />, [PageComponent]);
  const showTransition = ticking && duration - elapsed < 2600 && elapsed > 600;
  const showHint = page === 0 && !hasNavigated && !playing;
  return <SnakContext.Provider value={api} data-id="element-138" data-mp="3h5fiuxs6cw8z">
      <MotionConfig reducedMotion="user" data-id="element-139" data-mp="3h4cvv0xz3dyc">
        <div className={`theme-${PAGES[page].theme} snak-shell flex h-screen flex-col overflow-hidden`} data-id="element-140" data-mp="3m3h67uwmvhq9">
          <TopBar data-id="element-141" data-mp="25oxy3uxxwi26" />
          <main className="relative min-h-0 flex-1" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-id="element-142" data-mp="25ofrq5hkcngj">
            {/* Keyed remount with a CSS entrance: the new chapter mounts immediately and never waits on an exit animation. */}
            <div key={page} className="snak-page scroll-quiet absolute inset-0 overflow-y-auto" style={{
            "--dir": dir
          } as CSSProperties} data-id="element-143" data-mp="gag7g54eh8gf">
              {pageElement}
            </div>
            <TransitionLabel visible={showTransition || showHint} label={showHint ? "Swipe or tap Next — explore in any order" : PAGES[page].next} data-id="element-144" data-mp="2hgti7k9nmaz" />
          </main>
          <Controls elapsed={elapsed} pausedByExplore={pausedByExplore && !playing} data-id="element-145" data-mp="3onm1qk79suin" />
        </div>
        <DetailSheet detail={detail} onClose={() => setDetail(null)} data-id="element-146" data-mp="3joj0lauev8fy" />
        <ChapterSheet open={chaptersOpen} onClose={() => setChaptersOpen(false)} data-id="element-147" data-mp="3fngwogpju87n" />
        <ReferencesSheet open={refs.open} target={refs.target} onClose={() => setRefs(r => ({
        ...r,
        open: false
      }))} data-id="element-148" data-mp="3fpjydy02dps7" />
      </MotionConfig>
    </SnakContext.Provider>;
}